package com.evoapps.evoshift.spreadsheet

import androidx.compose.runtime.Composable
import androidx.compose.runtime.staticCompositionLocalOf
import com.evoapps.evoshift.domain.spreadsheet.SpreadsheetReader

/** A file the person picked or dropped: its name (for the screen) and its bytes, read on the device. */
class PickedFile(val name: String, val bytes: ByteArray)

/**
 * SH-123, shared since SH-214: the platform's file chooser for a spreadsheet (.csv, .tsv, .xlsx,
 * .txt): Android's document picker, iOS's Files, the desktop's Open dialog, the browser's file
 * input. At most [SpreadsheetReader.READ_LIMIT] bytes are read, so a huge file is refused without
 * being read whole; closing the chooser sends nothing. Used by the sales import, the staff import
 * and (SH-217) the roster import.
 */
fun interface SpreadsheetFilePicker {
    fun pick()
}

/** Called with the picked file, or with null when it could not be read. */
typealias OnFilePicked = (PickedFile?) -> Unit

@Composable
internal expect fun rememberPlatformSpreadsheetFilePicker(onPicked: OnFilePicked): SpreadsheetFilePicker

/** Replaces the platform chooser where there is no system UI (UI tests). */
val LocalSpreadsheetFilePickerOverride = staticCompositionLocalOf<((OnFilePicked) -> SpreadsheetFilePicker)?> { null }

@Composable
fun rememberSpreadsheetFilePicker(onPicked: OnFilePicked): SpreadsheetFilePicker {
    val override = LocalSpreadsheetFilePickerOverride.current
    return override?.invoke(onPicked) ?: rememberPlatformSpreadsheetFilePicker(onPicked)
}

/** What a picker or a drop reads at most: one byte over the reader's limit. */
internal const val PICK_READ_LIMIT = SpreadsheetReader.READ_LIMIT

/** The file types offered (extensions, lower case). */
internal val SPREADSHEET_FILE_EXTENSIONS = setOf("csv", "tsv", "xlsx", "txt")
